import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AnswerHint,
  AnswerList,
  Content,
  ErrorItem,
  ErrorList,
  Eyebrow,
  HeaderCopy,
  LoadingPanel,
  PageRoot,
  ProgressBlock,
  ProgressFill,
  ProgressMeta,
  ProgressTrack,
  ProgressValue,
  QuestionCard,
  QuestionContent,
  QuestionCounter,
  QuestionFooter,
  QuestionHeading,
  QuestionLabel,
  QuestionTitle,
  QuizActions,
  QuizDescription,
  QuizHeader,
  QuizTitle,
  ResultContainer,
  ResultCallout,
  ResultMark,
  ResultText,
  ResultTitle,
} from './styled';
import { Animation, Load } from 'ui/styled';
import { ReadOneQuiz } from 'services/quiz';
import { apiRequest, exposeStrapiError } from 'utils';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import Button from 'components/Form/Button';
import QuizAnswerOption from 'components/QuizAnswerOption';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { CreateQuizResponse } from 'services/quiz-response';
import { useAuthLayout } from 'context/AuthLayoutContext';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getPortalConfig } from 'utils/portal';
import { buildQuizSummary, findFirstUnansweredQuestion, mapQuizResult } from './helpers';
import { getSurveyRequirementError } from 'screens/AuthenticatedArea/Survey/helpers';
import { trackEvent } from 'services/analytics';
import useI18n from 'hooks/useI18n';
import TrainerSelectionCard from 'components/TrainerSelectionCard';
import { ReadActiveCertificateTrainers } from 'services/certificate-trainers';


export default function Quiz() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useI18n();
  const { setLayoutProps } = useAuthLayout();
  const portalConfig = useMemo(() => getPortalConfig(
    location.pathname.startsWith('/mvu-accelerator')
      ? 'accelerator'
      : location.pathname.startsWith('/mvu-lite') || location.pathname.startsWith('/mvu-essentials')
        ? 'essentials'
        : 'universe'
  ), [location.pathname]);

  const [quizzes, setQuizzes] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answersByQuestion, setAnswersByQuestion] = useState({});
  const [result, setResult] = useState(null);
  const [loadError, setLoadError] = useState(null)
  const [surveyRequirement, setSurveyRequirement] = useState(null)
  const [loading, setLoading] = useState(false)
  const [trainers, setTrainers] = useState([])
  const [trainersLoading, setTrainersLoading] = useState(false)
  const [trainersError, setTrainersError] = useState('')
  const [selectedTrainer, setSelectedTrainer] = useState('')
  const [trainerConfirmed, setTrainerConfirmed] = useState(false)

  const resetQuiz = useCallback(() => {
    setCurrentIndex(0);
    setAnswersByQuestion({});
    setResult(null);
  }, []);

  const { id } = useParams()

  const loadTrainers = useCallback(async () => {
    setTrainersLoading(true)
    setTrainersError('')
    const result = await apiRequest(() => ReadActiveCertificateTrainers())
    if (exposeStrapiError(result)) {
      setTrainersError('Trainers could not be loaded. Please try again.')
    } else {
      setTrainers(Array.isArray(result?.data) ? result.data : [])
    }
    setTrainersLoading(false)
  }, [])

  const quizInit = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await apiRequest(() => ReadOneQuiz(id))
      const requirement = getSurveyRequirementError(data)
      if (requirement) {
        trackEvent('survey_required_before_quiz', {
          content_type: 'survey',
          course_id: requirement.courseId,
        })
        setSurveyRequirement(requirement)
        setLoadError(t('survey_required_before_quiz'))
        return
      }
      if (exposeStrapiError(data)) {
        history.goBack()
        return
      }
      if (!Array.isArray(data) || !data[0]?.questions?.length) {
        setLoadError('This quiz is not available yet.')
        return
      }
      setQuizzes(data)
      if (portalConfig.id !== 'accelerator' && data[0]?.certification?.requiresTrainerApproval) {
        const storageKey = `certificate-trainer:${data[0].documentId || id}`
        setSelectedTrainer(window.sessionStorage.getItem(storageKey) || '')
        await loadTrainers()
      } else {
        setTrainerConfirmed(true)
      }
    } finally {
      setLoading(false)
    }
  }, [history, id, loadTrainers, portalConfig.id, t])

  useEffect(() => { quizInit() }, [quizInit])

  const question = useMemo(() => {
    if (quizzes) {
      return quizzes?.[0]?.questions?.[currentIndex]
    }
  }, [currentIndex, quizzes])

  const handleSelect = useCallback((answerId) => {
    if (!question) return;
    const qid = question?.id;
    const isMultiple = !!question?.multiple;
    setAnswersByQuestion((prev) => {
      const prevForQ = prev[qid] ?? [];
      if (!isMultiple) {
        const isSame = prevForQ.length === 1 && prevForQ[0] === answerId;
        return { ...prev, [qid]: isSame ? [] : [answerId] };
      }
      const exists = prevForQ.includes(answerId);
      const nextForQ = exists ? prevForQ.filter((id) => id !== answerId) : [...prevForQ, answerId];
      return { ...prev, [qid]: nextForQ };
    });
  }, [question]);

  const submitQuiz = useCallback(async () => {
    const quiz = quizzes?.[0];
    const questions = quiz?.questions ?? [];
    if (!questions.length) return;
    const firstUnansweredQuestion = findFirstUnansweredQuestion(questions, answersByQuestion)
    if (firstUnansweredQuestion >= 0) {
      setCurrentIndex(firstUnansweredQuestion)
      return
    }

    setLoading(true);
    try {
      const summary = buildQuizSummary(questions, answersByQuestion);
      const body = {
        summary,
        quiz: quiz?.documentId,
        ...(portalConfig.id !== 'accelerator' && quiz?.certification?.requiresTrainerApproval
          ? { certificateTrainer: selectedTrainer }
          : {}),
      };

      const resp = await apiRequest(() => CreateQuizResponse(body))
      const requirement = getSurveyRequirementError(resp)
      if (requirement) {
        trackEvent('survey_required_before_quiz', {
          content_type: 'survey',
          course_id: requirement.courseId,
        })
        setSurveyRequirement(requirement)
        setLoadError(t('survey_required_before_quiz'))
        setResult(null)
        return
      }
      if (exposeStrapiError(resp)) {
        setLoading(false);
        return;
      }

      const mapped = mapQuizResult(resp, quiz);
      setResult(mapped);
    } catch (e) {
      console.error('submitQuiz error', e);
    } finally {
      setLoading(false);
    }
  }, [answersByQuestion, quizzes, portalConfig.id, selectedTrainer, t]);

  const confirmTrainer = useCallback((trainerDocumentId) => {
    const quizDocumentId = quizzes?.[0]?.documentId || id
    window.sessionStorage.setItem(`certificate-trainer:${quizDocumentId}`, trainerDocumentId)
    setSelectedTrainer(trainerDocumentId)
    setTrainerConfirmed(true)
  }, [id, quizzes])

  const selectTrainer = useCallback((trainerDocumentId) => {
    const quizDocumentId = quizzes?.[0]?.documentId || id
    window.sessionStorage.setItem(`certificate-trainer:${quizDocumentId}`, trainerDocumentId)
    setSelectedTrainer(trainerDocumentId)
  }, [id, quizzes])

  const selectedForCurrent = answersByQuestion[question?.id] ?? [];
  const totalQuestions = quizzes?.[0]?.questions?.length || 0;
  const isLastQuestion = totalQuestions - 1 === currentIndex;
  const displayedQuestion = result ? totalQuestions : currentIndex + 1;
  const progressPercentage = totalQuestions ? (displayedQuestion / totalQuestions) * 100 : 0;
  const pendingApproval = result?.isApproved
    && ['pending', 'pending_trainer_approval', 'processing'].includes(result.certificateApproval?.status)

  const nextQuestion = () => {
    if (!selectedForCurrent.length) return
    if (!isLastQuestion) {
      setCurrentIndex(currentIndex + 1)
      return;
    }
    submitQuiz()
  }

  const back = () => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  }

  useEffect(() => {
    setLayoutProps({ headerSecondary: true });
  }, [setLayoutProps]);
  return (
    <PageRoot>
      {loading ? (
        <LoadingPanel>
          <Load white />
          Loading quiz…
        </LoadingPanel>
      ) : (
        <Content>
          {quizzes?.[0] ? (
            <QuizHeader>
              <HeaderCopy>
                <Eyebrow>Knowledge check</Eyebrow>
                <QuizTitle>{quizzes[0].title}</QuizTitle>
                <QuizDescription>{quizzes[0].description}</QuizDescription>
              </HeaderCopy>
              <ProgressBlock>
                <ProgressMeta>
                  <span>Quiz progress</span>
                  <ProgressValue>{Math.round(progressPercentage)}%</ProgressValue>
                </ProgressMeta>
                <ProgressTrack
                  role="progressbar"
                  aria-label="Quiz progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={Math.round(progressPercentage)}
                >
                  <ProgressFill $progress={progressPercentage} />
                </ProgressTrack>
              </ProgressBlock>
            </QuizHeader>
          ) : null}
          {portalConfig.id !== 'accelerator' && quizzes?.[0]?.certification?.requiresTrainerApproval && !trainerConfirmed ? (
            <TrainerSelectionCard
              trainers={trainers}
              selectedTrainer={selectedTrainer}
              loading={trainersLoading}
              error={trainersError}
              onContinue={confirmTrainer}
              onSelectionChange={selectTrainer}
              onRetry={loadTrainers}
            />
          ) : loadError ? (
            <ResultContainer>
              <ResultMark>!</ResultMark>
              <ResultTitle>{surveyRequirement ? t('survey_required_title') : 'Quiz unavailable'}</ResultTitle>
              <ResultText>{loadError}</ResultText>
              <Button
                color="blue"
                nospace
                width="fit-content"
                onClick={() => surveyRequirement?.courseId
                  ? history.push(portalConfig.buildSurveyRoute(surveyRequirement.courseId))
                  : history.goBack()}
              >
                {surveyRequirement ? t('survey_open') : 'Back to Course'}
              </Button>
            </ResultContainer>
          ) : !result ? (
            <QuestionCard key={question?.id || currentIndex}>
              <QuestionContent>
                <QuestionHeading>
                  <div>
                    <QuestionLabel>Question {currentIndex + 1}</QuestionLabel>
                    <QuestionTitle>{question?.question}</QuestionTitle>
                  </div>
                  <QuestionCounter aria-label={`Question ${currentIndex + 1} of ${totalQuestions}`}>
                    {String(currentIndex + 1).padStart(2, '0')} / {String(totalQuestions).padStart(2, '0')}
                  </QuestionCounter>
                </QuestionHeading>
                <AnswerList>
                  {question?.answers?.map((m) => (
                    <QuizAnswerOption
                      key={m?.id}
                      label={m?.title}
                      multiple={!!question?.multiple}
                      selected={selectedForCurrent.includes(m?.id)}
                      onSelect={() => handleSelect(m?.id)}
                    />
                  ))}
                </AnswerList>
              </QuestionContent>
              <QuestionFooter>
                <AnswerHint>
                  {question?.multiple ? 'Select all answers that apply.' : 'Select one answer to continue.'}
                </AnswerHint>
                <QuizActions>
                  <Button outline color="white" nospace width="fit-content" onClick={back} disabled={currentIndex === 0}>Back</Button>
                  <Button color="blue" nospace width="fit-content" onClick={nextQuestion} disabled={!selectedForCurrent.length}>
                    {isLastQuestion ? 'Finish test' : 'Next question'}
                  </Button>
                </QuizActions>
              </QuestionFooter>
            </QuestionCard>
          ) : (
            <ResultContainer>
              {result.isApproved ? (
                <>
                  <ResultMark $success={!pendingApproval} $pending={pendingApproval}>{pendingApproval ? '…' : '✓'}</ResultMark>
                  <ResultTitle>{pendingApproval ? 'Certification pending approval' : 'Excellent job!'}</ResultTitle>
                  {pendingApproval ? (
                    <>
                      <ResultText>Your quiz has been successfully completed.</ResultText>
                      <ResultCallout>
                        <span>Your certification has been sent to</span>
                        <strong>{result.certificateApproval?.trainer?.name || 'your trainer'}</strong>
                        <span>We’ll notify you as soon as it has been reviewed.</span>
                      </ResultCallout>
                    </>
                  ) : (
                    <ResultText>{portalConfig.id === 'accelerator' ? 'Great work! You have completed the test.' : 'Great work! Return to the course details to download your certificate.'}</ResultText>
                  )}
                  <Animation width={190} animationData={require('assets/lotties/success.json')} />
                  <QuizActions>
                    <Button outline color="white" nospace width="fit-content" onClick={() => history.goBack()}>Back to Course</Button>
                    <Button color="blue" nospace width="fit-content" onClick={() => history.push(portalConfig.homeRoute)}>Back to Home</Button>
                  </QuizActions>
                </>
              ) : (
                <>
                  <ResultMark>!</ResultMark>
                  <ResultTitle>Sorry! You didn’t reach the minimum score.</ResultTitle>
                  <ResultText>
                    You scored {Math.round(result.achievedPercentage)}% (minimum {Math.round(result.requiredPercentage)}%).
                  </ResultText>
                  {typeof result.remainingAttempts === 'number' && (
                    <ResultText>Attempts remaining today: {result.remainingAttempts}</ResultText>
                  )}
                  {result.blocked && result.message && (
                    <>
                      <ResultText>{result.message}</ResultText>
                      <ResultText>You can only make 2 attempts per day.</ResultText>
                    </>
                  )}
                  <ResultText>Review the questions you missed:</ResultText>
                  <ErrorList>
                    {result.incorrectQuestions.map((q) => (
                      <ErrorItem key={q.id}>
                        <strong>{q.question}</strong>
                        {q.lessonTitle ? (
                          <div>Recommendation: review the lesson "<em>{q.lessonTitle}</em>".</div>
                        ) : null}
                      </ErrorItem>
                    ))}
                  </ErrorList>
                  <QuizActions>
                    <Button
                      outline
                      color="white"
                      nospace
                      width="fit-content"
                      onClick={resetQuiz}
                      disabled={result.remainingAttempts === 0}
                      title={result.remainingAttempts === 0 ? 'No attempts left today' : undefined}
                    >
                      Try again
                    </Button>
                    <Button color="blue" nospace width="fit-content" onClick={() => history.push(portalConfig.homeRoute)}>Back to Home</Button>
                  </QuizActions>
                </>
              )}
            </ResultContainer>
          )}
        </Content>
      )}
    </PageRoot>

  )
}
